import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Bot,
  ChevronDown,
  Compass,
  Gem,
  Gift,
  Globe,
  Images,
  LogIn,
  LogOut,
  Moon,
  Shield,
  Sparkles,
  Sun,
  User,
  UserPlus,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/flowly/infra/Button/Button";
import { NavCreditsDisplay } from "./NavCreditsDisplay";
import { NavPricingBadge } from "./NavPricingBadge";
import { useAuthModal } from "@/contexts/AuthModalContext";
import { useTheme } from "@/contexts/ThemeContext";
import { APP_VERSION_DISPLAY } from "@/config/version";
import { COMMUNITY_ENABLED, SIGNUPS_ENABLED } from "@/config/featureFlags";
import { cn } from "@/lib/utils";
import type { UserDisplayInfo } from "./types";

interface NavMobileMenuProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  isActive: (path: string) => boolean;
  user: unknown;
  authLoading: boolean;
  isAdmin: boolean;
  handleSignOut: () => Promise<void>;
  userDisplayInfo: UserDisplayInfo;
  credits: number;
  maxCredits: number;
  creditsPercentage: number;
  isRTL: boolean;
  t: {
    nav: {
      explore: string;
      profile: string;
      myBot: string;
      betaSection: string;
      inviteFriends: string;
      login: string;
      signup: string;
      pricing: string;
      gallery: string;
      createNow: string;
      createNowFree: string;
      logout: string;
      credits: string;
      remaining: string;
      plan: string;
      menu: string;
      lightMode: string;
      darkMode: string;
      admin: string;
    };
  };
}

export function NavMobileMenu({
  isOpen,
  setIsOpen,
  isActive,
  user,
  authLoading,
  isAdmin,
  handleSignOut,
  userDisplayInfo,
  credits,
  maxCredits,
  creditsPercentage,
  isRTL,
  t,
}: NavMobileMenuProps) {
  const { openAuthModal } = useAuthModal();
  const { theme, toggleTheme } = useTheme();
  const { language, setLanguage, localizePath } = useLanguage();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Switch language AND navigate to the language-specific URL. Setting state
  // alone would leave a /he/* user on /he/* with English UI (mismatch), or
  // an English-path user on /pricing with Hebrew UI (also mismatch and
  // breaks the prerender SEO benefit). Always pair the two operations.
  const toggleLanguage = () => {
    const next = language === "he" ? "en" : "he";
    setLanguage(next);
    navigate(localizePath(pathname, next));
  };
  // Beta section starts collapsed — same UX as the desktop popover. Future
  // beta items go inside this group.
  const [betaOpen, setBetaOpen] = useState(false);

  const closeMenu = () => setIsOpen(false);

  const navLinkClass = (path: string) =>
    cn(
      "flex items-center gap-3 px-3 py-3 rounded-lg text-base transition-colors",
      isActive(path)
        ? "bg-secondary/50 text-foreground font-medium"
        : "text-muted-foreground hover:text-foreground hover:bg-secondary/30",
    );

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent
        side={isRTL ? "left" : "right"}
        className="flex flex-col p-0 w-[85vw] sm:max-w-sm [&>button]:rtl:right-auto [&>button]:rtl:left-4"
      >
        <SheetTitle className="sr-only">{t.nav.menu}</SheetTitle>

        <div className="flex flex-col flex-1 overflow-y-auto">
          {!authLoading && user ? (
            <>
              {/* Profile header */}
              <div className="flex items-center gap-3 p-5 pb-4">
                <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-primary/30 flex-shrink-0">
                  <img
                    src={userDisplayInfo.avatarUrl}
                    alt={userDisplayInfo.displayName}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-foreground truncate">
                    {userDisplayInfo.displayName}
                  </p>
                  <p className="text-xs text-muted-foreground capitalize">
                    {t.nav.plan} {userDisplayInfo.planName}
                  </p>
                </div>
              </div>

              {/* Credits */}
              <div className="px-5">
                <NavCreditsDisplay
                  credits={credits}
                  maxCredits={maxCredits}
                  creditsPercentage={creditsPercentage}
                  creditsLabel={t.nav.credits}
                  remainingLabel={t.nav.remaining}
                  className="border-t-0 pt-0"
                />
              </div>

              <Separator className="my-3" />

              {/* Create CTA */}
              <div className="px-5">
                <Link to="/generate-product-catalog" onClick={closeMenu}>
                  <Button
                    variant="accent"
                    size="md"
                    className="w-full"
                  >
                    <Sparkles className="w-4 h-4" />
                    {t.nav.createNowFree}
                  </Button>
                </Link>
              </div>

              <Separator className="my-3" />

              {/* Nav links */}
              <nav className="px-3 flex flex-col gap-1">
                {COMMUNITY_ENABLED && (
                  <Link
                    to="/dashboard"
                    onClick={closeMenu}
                    className={navLinkClass("/dashboard")}
                  >
                    <Compass className="w-5 h-5" />
                    {t.nav.explore}
                  </Link>
                )}
                <Link
                  to="/pricing"
                  onClick={closeMenu}
                  className={navLinkClass("/pricing")}
                >
                  <Gem className="w-5 h-5 text-brand" />
                  <span className="flex flex-col items-start gap-1">
                    {t.nav.pricing}
                    <NavPricingBadge floating={false} />
                  </span>
                </Link>
                <Link
                  to="/gallery"
                  onClick={closeMenu}
                  className={navLinkClass("/gallery")}
                >
                  <Images className="w-5 h-5" />
                  {t.nav.gallery}
                </Link>
              </nav>

              <Separator className="my-3" />

              {/* Account */}
              <nav className="px-3 flex flex-col gap-1">
                <Link
                  to="/profile"
                  onClick={closeMenu}
                  className={navLinkClass("/profile")}
                >
                  <User className="w-5 h-5" />
                  {t.nav.profile}
                </Link>
                {/* Beta section — trigger mirrors the link rows visually
                    (same padding, font, hover); sub-items reuse navLinkClass
                    so the panel stays uniform. */}
                <button
                  type="button"
                  onClick={() => setBetaOpen((v) => !v)}
                  className="flex items-center justify-between gap-3 px-3 py-3 rounded-lg text-base text-muted-foreground hover:text-foreground hover:bg-secondary/30 transition-colors w-full"
                  aria-expanded={betaOpen}
                >
                  <span className="flex items-center gap-3">
                    <Sparkles className="w-5 h-5 text-primary" />
                    {t.nav.betaSection}
                  </span>
                  <ChevronDown
                    className={cn(
                      "w-4 h-4 transition-transform",
                      betaOpen && "rotate-180",
                    )}
                  />
                </button>
                {betaOpen && (
                  <Link
                    to="/bot-builder"
                    onClick={closeMenu}
                    className={navLinkClass("/bot-builder")}
                  >
                    <Bot className="w-5 h-5" />
                    {t.nav.myBot}
                  </Link>
                )}
                <Link
                  to="/invite"
                  onClick={closeMenu}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg text-base text-primary hover:bg-primary/10 transition-colors"
                >
                  <Gift className="w-5 h-5" />
                  {t.nav.inviteFriends}
                </Link>
                {isAdmin && (
                  <Link
                    to="/admin"
                    onClick={closeMenu}
                    className={navLinkClass("/admin")}
                  >
                    <Shield className="w-5 h-5" />
                    {t.nav.admin}
                  </Link>
                )}
              </nav>

              {/* Spacer + footer */}
              <div className="flex-1" />

              <div className="px-3 pb-5">
                <button
                  onClick={toggleLanguage}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg text-base text-muted-foreground hover:text-foreground hover:bg-secondary/30 transition-colors w-full"
                >
                  <Globe className="w-5 h-5" />
                  {language === "he" ? "English" : "עברית"}
                </button>
                <button
                  onClick={toggleTheme}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg text-base text-muted-foreground hover:text-foreground hover:bg-secondary/30 transition-colors w-full"
                >
                  {theme === "dark" ? (
                    <Sun className="w-5 h-5" />
                  ) : (
                    <Moon className="w-5 h-5" />
                  )}
                  {theme === "dark" ? t.nav.lightMode : t.nav.darkMode}
                </button>
                <button
                  onClick={() => {
                    closeMenu();
                    handleSignOut();
                  }}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg text-base text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors w-full"
                >
                  <LogOut className="w-5 h-5" />
                  {t.nav.logout}
                </button>
                <p className="text-[10px] text-muted-foreground/50 text-center pt-2">
                  {APP_VERSION_DISPLAY}
                </p>
              </div>
            </>
          ) : !authLoading ? (
            <>
              {/* Unauthenticated: CTA */}
              <div className="p-5">
                <Button
                  variant="accent"
                  size="md"
                  onClick={() => {
                    closeMenu();
                    openAuthModal("login");
                  }}
                  className="w-full"
                >
                  <Sparkles className="w-4 h-4" />
                  {t.nav.createNowFree}
                </Button>
              </div>

              <Separator />

              {/* Nav links */}
              <nav className="px-3 py-3 flex flex-col gap-1">
                {COMMUNITY_ENABLED && (
                  <Link
                    to="/dashboard"
                    onClick={closeMenu}
                    className={navLinkClass("/dashboard")}
                  >
                    <Compass className="w-5 h-5" />
                    {t.nav.explore}
                  </Link>
                )}
                <Link
                  to="/pricing"
                  onClick={closeMenu}
                  className={navLinkClass("/pricing")}
                >
                  <Gem className="w-5 h-5 text-brand" />
                  <span className="flex flex-col items-start gap-1">
                    {t.nav.pricing}
                    <NavPricingBadge floating={false} />
                  </span>
                </Link>
              </nav>

              <Separator />

              {/* Theme toggle */}
              <div className="px-3 py-3">
                <button
                  onClick={toggleTheme}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg text-base text-muted-foreground hover:text-foreground hover:bg-secondary/30 transition-colors w-full"
                >
                  {theme === "dark" ? (
                    <Sun className="w-5 h-5" />
                  ) : (
                    <Moon className="w-5 h-5" />
                  )}
                  {theme === "dark" ? t.nav.lightMode : t.nav.darkMode}
                </button>
              </div>

              <Separator />

              {/* Auth actions */}
              <div className="px-3 py-3 flex flex-col gap-1">
                <button
                  onClick={() => {
                    closeMenu();
                    openAuthModal("login");
                  }}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg text-base text-muted-foreground hover:text-foreground hover:bg-secondary/30 transition-colors w-full"
                >
                  <LogIn className="w-5 h-5" />
                  {t.nav.login}
                </button>
                {/* Signup hidden during the wind-down — login only. */}
                {SIGNUPS_ENABLED && (
                  <button
                    onClick={() => {
                      closeMenu();
                      openAuthModal("signup");
                    }}
                    className="flex items-center gap-3 px-3 py-3 rounded-lg text-base text-muted-foreground hover:text-foreground hover:bg-secondary/30 transition-colors w-full"
                  >
                    <UserPlus className="w-5 h-5" />
                    {t.nav.signup}
                  </button>
                )}
              </div>
            </>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}
